<script setup>
import { computed } from 'vue';
import SummaryReportLink from './SummaryReportLink.vue';
import SummaryArchiveLink from './SummaryArchiveLink.vue';
import { formatReportDuration } from '../constants';

const props = defineProps({
  rows: {
    type: Array,
    default: () => [],
  },
  archiveContext: {
    type: Object,
    default: () => ({}),
  },
});

const formatCount = value => (value ? value.toLocaleString() : '--');

const formatTime = value => formatReportDuration(value);

const formatCsat = (average, responses) => {
  if (average === null || average === undefined || average === '') return '--';
  return `${Number(average).toFixed(1)}/${responses || 0}`;
};

const rawValueFor = (row, metricKey) => {
  if (metricKey === 'resolutionsCount') {
    return row.resolvedConversationsCount ?? row.resolved_conversations_count;
  }
  if (metricKey === 'assignmentEpisodesCount') {
    return row.assignmentEpisodesCount ?? row.assignment_episodes_count;
  }
  if (metricKey === 'transfersOutCount') {
    return row.transfersOutCount ?? row.transfers_out_count;
  }
  if (metricKey === 'firstResponseCount') {
    const seconds =
      row.medianFirstResponseTime ?? row.median_first_response_time;
    return seconds === null || seconds === undefined || seconds === '' ? 0 : 1;
  }
  if (metricKey === 'replyTimeCount') {
    const seconds = row.avgReplyTime ?? row.avg_reply_time;
    return seconds === null || seconds === undefined || seconds === '' ? 0 : 1;
  }
  if (metricKey === 'csatResponses') {
    return row.csatResponses ?? row.csat_responses;
  }
  return row[metricKey];
};

const tableRows = computed(() =>
  props.rows.map(row => ({
    id: row.id,
    name: row.name,
    deleted: row.deleted,
    type: 'agent',
    assignmentEpisodesCount: formatCount(
      row.assignmentEpisodesCount || row.uniqueCyclesHandled
    ),
    resolvedConversationsCount: formatCount(row.resolvedConversationsCount),
    transfersOutCount: formatCount(row.transfersOutCount),
    medianFirstResponseTime: formatTime(row.medianFirstResponseTime),
    avgReplyTime: formatTime(row.avgReplyTime),
    csat: formatCsat(row.csatAverage, row.csatResponses),
    raw: row,
  }))
);

const columns = [
  { key: 'name', labelKey: 'AGENT' },
  {
    key: 'assignmentEpisodesCount',
    labelKey: 'ACCEPTED_EPISODES',
    metricKey: 'assignmentEpisodesCount',
  },
  {
    key: 'resolvedConversationsCount',
    labelKey: 'RESOLUTION_COUNT',
    metricKey: 'resolutionsCount',
  },
  {
    key: 'transfersOutCount',
    labelKey: 'TRANSFERS_OUT',
    metricKey: 'transfersOutCount',
  },
  {
    key: 'medianFirstResponseTime',
    labelKey: 'MEDIAN_FIRST_RESPONSE_TIME',
    metricKey: 'firstResponseCount',
  },
  {
    key: 'avgReplyTime',
    labelKey: 'AVG_REPLY_TIME',
    metricKey: 'replyTimeCount',
  },
  {
    key: 'csat',
    labelKey: 'CSAT',
    metricKey: 'csatResponses',
  },
];
</script>

<template>
  <section
    v-if="tableRows.length"
    class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 overflow-auto"
  >
    <h3 class="text-base font-medium text-n-slate-12">
      {{ $t('SUMMARY_REPORTS.CHARTS.TEAM_OPERATORS_TABLE') }}
    </h3>
    <table class="mt-4 min-w-full text-sm">
      <thead>
        <tr>
          <th
            v-for="column in columns"
            :key="column.key"
            class="p-2 text-left text-xs font-medium uppercase tracking-wider text-n-slate-11"
          >
            {{ $t(`SUMMARY_REPORTS.${column.labelKey}`) }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in tableRows"
          :key="row.id"
          class="border-t border-n-weak hover:bg-n-alpha-1"
        >
          <td
            v-for="column in columns"
            :key="column.key"
            class="p-2 text-n-slate-12"
          >
            <SummaryReportLink
              v-if="column.key === 'name'"
              :row="{ original: row }"
              compact
            />
            <SummaryArchiveLink
              v-else-if="column.metricKey"
              :value="row[column.key]"
              :metric-key="column.metricKey"
              :row="row.raw"
              :archive-context="archiveContext"
              :raw-value="rawValueFor(row.raw, column.metricKey)"
            />
            <span v-else class="tabular-nums">{{ row[column.key] }}</span>
          </td>
        </tr>
      </tbody>
    </table>
  </section>
</template>
